import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirmAction } from '../../components/ConfirmDialog';
import { employerAPI } from '../../services/api';
import { FaBuilding, FaArrowLeft, FaCheckCircle, FaTimesCircle, FaFileAlt, FaUserShield } from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner
} from '../../components/dashboard';

const ReviewEmployer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [employer, setEmployer] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadEmployer();
    }, [id]);

    const loadEmployer = async () => {
        try {
            setLoading(true);
            const response = await employerAPI.getAll({});
            const employers = response.employers || response || [];
            const found = employers.find(e => e.id === parseInt(id));
            if (found) {
                setEmployer(found);
            } else {
                toast.error('Employer not found');
                navigate('/admin/employers');
            }
        } catch (err) {
            console.error('Failed to load employer:', err);
            toast.error('Failed to load employer details.');
            navigate('/admin/employers');
        } finally {
            setLoading(false);
        }
    };

    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);

    const handleApproveKYC = async () => {
        const confirmed = await confirmAction('Approve this employer KYC?');
        if (!confirmed) return;

        try {
            await employerAPI.reviewKYC(id, { action: 'approve' });
            toast.success('KYC approved successfully!');
            loadEmployer(); // Reload to show updated status
        } catch (err) {
            console.error('Failed to approve KYC:', err);
            toast.error(err?.response?.data?.error || 'Failed to approve KYC. Please try again.');
        }
    };

    const handleRejectKYC = async () => {
        if (!rejectionReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        const confirmed = await confirmAction('Reject this employer KYC?');
        if (!confirmed) return;

        try {
            await employerAPI.reviewKYC(id, { action: 'reject', rejection_reason: rejectionReason });
            toast.success('KYC rejected');
            setShowRejectForm(false);
            setRejectionReason('');
            loadEmployer(); // Reload to show updated status
        } catch (err) {
            console.error('Failed to reject KYC:', err);
            toast.error(err?.response?.data?.error || 'Failed to reject KYC. Please try again.');
        }
    };

    const handleApprove = async () => {
        const confirmed = await confirmAction('Approve this employer?');
        if (!confirmed) return;

        try {
            await employerAPI.approve(id);
            toast.success('Employer approved successfully!');
            navigate('/admin/employers');
        } catch (err) {
            console.error('Failed to approve employer:', err);
            toast.error('Failed to approve employer. Please try again.');
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!employer) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=employers" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Dashboard</span>
                </Link>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="flex items-center justify-between mb-6">
                        <DashboardHeader
                            icon={FaBuilding}
                            title="Review Employer"
                            subtitle="Review employer details and approve registration"
                        />
                        <StatusBadge status={employer.is_approved ? 'approved' : 'pending'} />
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                            <p className="text-lg font-semibold text-gray-900">{employer.company_name}</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <p className="text-gray-700">{employer.email}</p>
                        </div>

                        {employer.company_description && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <p className="text-gray-700">{employer.company_description}</p>
                            </div>
                        )}

                        {/* KYC Information Section */}
                        <div className="border-t border-gray-200 pt-6 mt-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <FaUserShield /> KYC Verification
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">KYC Status</label>
                                    <StatusBadge status={employer.kyc_status || 'pending'} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Submitted Date</label>
                                    <p className="text-gray-700">
                                        {employer.kyc_submitted_at ? new Date(employer.kyc_submitted_at).toLocaleDateString() : 'Not submitted'}
                                    </p>
                                </div>
                            </div>

                            {/* KYC Details */}
                            {employer.kyc_status === 'submitted' || employer.kyc_status === 'under_review' || employer.kyc_status === 'approved' || employer.kyc_status === 'rejected' ? (
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span className="font-medium text-gray-700">Company Registration #:</span>
                                            <p className="text-gray-900">{employer.company_registration_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Representative:</span>
                                            <p className="text-gray-900">{employer.representative_name || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">ID Type:</span>
                                            <p className="text-gray-900">{employer.representative_id_type || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">ID Number:</span>
                                            <p className="text-gray-900">{employer.representative_id_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Tax ID:</span>
                                            <p className="text-gray-900">{employer.tax_id_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <span className="font-medium text-gray-700">Business License:</span>
                                            <p className="text-gray-900">{employer.business_license_number || 'N/A'}</p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <span className="font-medium text-gray-700">Company Address:</span>
                                            <p className="text-gray-900">
                                                {[employer.company_address, employer.company_city, employer.company_state, employer.company_country].filter(Boolean).join(', ') || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-600 text-sm mb-4">KYC information not yet submitted.</p>
                            )}

                            {employer.kyc_rejection_reason && (
                                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                                    <p className="text-sm font-medium text-red-800 mb-1">Rejection Reason:</p>
                                    <p className="text-sm text-red-700">{employer.kyc_rejection_reason}</p>
                                </div>
                            )}

                            {/* KYC Review Actions */}
                            {(employer.kyc_status === 'submitted' || employer.kyc_status === 'under_review') && (
                                <div className="space-y-3">
                                    {!showRejectForm ? (
                                        <div className="flex gap-3">
                                            <button
                                                onClick={handleApproveKYC}
                                                className="flex-1 px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <FaCheckCircle /> Approve KYC
                                            </button>
                                            <button
                                                onClick={() => setShowRejectForm(true)}
                                                className="flex-1 px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <FaTimesCircle /> Reject KYC
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Rejection Reason <span className="text-red-500">*</span>
                                                </label>
                                                <textarea
                                                    value={rejectionReason}
                                                    onChange={(e) => setRejectionReason(e.target.value)}
                                                    rows="3"
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-red-600"
                                                    placeholder="Please provide a reason for rejection..."
                                                />
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => {
                                                        setShowRejectForm(false);
                                                        setRejectionReason('');
                                                    }}
                                                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={handleRejectKYC}
                                                    className="flex-1 px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                                                >
                                                    Submit Rejection
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Status</label>
                                <StatusBadge status={employer.compliance_status || 'pending'} />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Date</label>
                                <p className="text-gray-700">
                                    {employer.created_at ? new Date(employer.created_at).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>

                        {!employer.is_approved && employer.kyc_status === 'approved' && (
                            <div className="pt-6 border-t border-gray-200">
                                <button
                                    onClick={handleApprove}
                                    className="w-full px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                                >
                                    <FaCheckCircle /> Approve Employer
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewEmployer;
